/* eslint-disable no-console */
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  ReloadOutlined,
  SyncOutlined,
  UploadOutlined
} from '@ant-design/icons';
import {Button, Cascader, Flex, Select, Table, Tag, Upload} from 'antd';
import moment from 'moment';
import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';

import BlockAccessPage from '../../components/BlockAccess/BlockAccessPage';
import {filterSelectOptions} from '../../components/Forms/Utils/commonFunctions';
import Loader from '../../components/Loader/Loader';
import PageContent from '../../components/PageContent/PageContent';
import {allRoutes} from '../../constants/Routes';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import electricBoard from '../../services/modules/JSON/electricBoard';
import OCRAPI from '../../services/modules/OCR/OCR';
import facilityDetailsAPI from '../../services/modules/OM/facilityDetails';
import {uploadDocsFile} from '../../services/modules/UploadAndJobs/uploadDocs';
import useCompanyStore, {
  accessModule
} from '../../store/companyStore/companyDetailsStore';
import {eachWordCap} from '../../utils/FormUtils/FirstCap';
import {yearSelect} from '../../utils/FormUtils/yearSelect';
import showMessage from '../../utils/showMessage';
import {shouldBlockModuleAccess} from '../../utils/SubscriptionHelper';

import {submitConfigOptions} from './constants/commonConstants';
import {filterOCRData} from './utils/commonFunction';

const OcrMain: React.FC = () => {
  const [uploadType, setUploadType] = useState<any>('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [refresh, setRefresh] = useState(false);
  const [facilityId, setFacilityId] = useState<string | undefined>(undefined);
  const firstYear = yearSelect();
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedYear, setSelectedYear] = useState<any>('');
  const [facilitySelected, setFacilitySelected] = useState<any | undefined>(
    undefined
  );
  const navigate = useNavigate();

  const RenderTags = (tag: string) => {
    switch (tag.toLocaleLowerCase()) {
      case 'ready_for_review':
      case 'reviewed':
        return {color: 'green', icon: <CheckCircleOutlined />};
      case 'uploaded':
        return {color: 'default', icon: <CheckCircleOutlined />};
      case 'in_progress':
        return {color: 'processing', icon: <SyncOutlined spin />};
      case 'failed':
        return {color: 'error', icon: <CloseCircleOutlined />};
      default:
        return {color: 'error', icon: <CloseCircleOutlined />};
    }
  };

  useEffect(() => {
    if (shouldBlockModuleAccess(accessModule.AIDocumentManager)) {
      setIsLoading(false);

      return;
    }
    setSelectedYear(searchParams.get('selectedYear') ?? firstYear?.[0].value);
    searchParams.set(
      'selectedYear',
      searchParams.get('selectedYear') ?? firstYear?.[0].value
    );
    if (searchParams.get('facilityId')) {
      setFacilityId(searchParams.get('facilityId') ?? undefined);
      setFacilitySelected(
        facilityDetails.find((item: any) => {
          return item.value === searchParams.get('facilityId');
        })
      );
    }
    // if (searchParams.get('facilityId')) {
    //   searchParams.set('facilityId', searchParams.get('facilityId'));
    // }
    setSearchParams(searchParams);
    setRefresh(true);
  }, []);

  useEffect(() => {
    if (shouldBlockModuleAccess(accessModule.AIDocumentManager)) {
      setIsLoading(false);

      return;
    }

    if (refresh) {
      if (facilityId && selectedYear) {
        setRefresh(true);
        OCRAPI(facilityId, selectedYear)
          .then((res) => {
            setDataSource(res);
            setRefresh(false);
          })
          .catch(() => {});
      } else {
        setRefresh(false);
      }
    }
  }, [refresh]);

  const columns = [
    {
      title: 'Sr No',
      dataIndex: '',
      render: (value: any, record: any, index: number) => {
        return ++index;
      }
    },
    {
      title: 'File name',
      dataIndex: 'file_name'
    },
    {
      title: 'Document Type',
      dataIndex: 'doc_type',
      render: (value: any) => {
        const displayValue = value?.split('_') ?? '';

        return eachWordCap(displayValue.join(' '));
      }
    },
    {
      title: 'Uploaded at',
      dataIndex: 'uploaded_at',
      render: (value: any) => {
        return moment(value ?? undefined).format('Do MMM YY h:mm a');
      }
    },
    {
      title: 'Processing time',
      dataIndex: 'processing_start_time',
      render: (value: any, record: any) => {
        const sec =
          moment(record.processing_end_time).diff(
            record.processing_start_time,
            'milliseconds'
          ) / 1000;

        return isNaN(sec) ? '' : `${sec}s`;
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (value: string) => {
        const render: {color: string; icon: JSX.Element} = RenderTags(value);

        return (
          <Tag icon={render.icon} color={render.color} key={value}>
            {value?.replaceAll('_', ' ').toUpperCase()}
          </Tag>
        );
      }
    },
    {
      title: 'Action',
      render: (value: any, record: any) => {
        return (
          <Button
            type="text"
            icon={<EyeOutlined />}
            disabled={!(record.status === 'ready_for_review')}
            onClick={() => {
              const showDataFilter = filterOCRData(record);
              const bbData = {
                bounding: showDataFilter.fields
                  .map((item: any) => {
                    return Array.isArray(item.value)
                      ? item.boundingBox.flat()
                      : item.boundingBox;
                  })
                  .filter((item: any) => {
                    return item;
                  })
                  .flat(),
                polygon: showDataFilter.fields
                  .map((item: any) => {
                    return item.name === 'items' && Array.isArray(item.value)
                      ? item.polygon.flat()
                      : item.polygon;
                  })
                  .filter((item: any) => {
                    return item;
                  })
                  .flat()
              };
              // setBB(bbData);
              navigate(
                {
                  pathname: `${allRoutes.OCR.OCRdisplay}`,
                  search: location.search
                },
                {
                  state: {
                    showData: showDataFilter,
                    bb: bbData,
                    facilityDetails: facilitySelected,
                    original: record.fields
                  }
                }
              );
              // setShowModel(true);
            }}
          />
        );
      }
    }
  ];

  const facilityDetails = useMemo(() => {
    return (
      useCompanyStore
        .getState()
        .facilityDetails?.[selectedYear]?.map((item: any) => {
          return {
            isLeased: item.isLeased,
            country: item.country,
            type: item.type,
            label: item.name,
            value: item._id
          };
        }) ?? []
    );
  }, [selectedYear]);

  useEffect(() => {
    if (shouldBlockModuleAccess(accessModule.AIDocumentManager)) {
      setIsLoading(false);

      return;
    }
    Promise.all([
      facilityDetailsAPI(APITypeEnum.GET_ALL, true),
      electricBoard()
    ])
      .then(() => {
        setIsLoading(false);
      })
      .catch(() => {});
  }, []);

  const handleSearch = (event: {name: string; value: string}) => {
    const {name, value} = event;
    if (value) {
      searchParams.set(name, value);
      setSearchParams(searchParams);
    } else {
      searchParams.delete(name);
      setSearchParams(searchParams);
    }
  };

  if (shouldBlockModuleAccess(accessModule.AIDocumentManager)) {
    return (
      <BlockAccessPage
        style={{
          width: '100%',
          height: '100%',
          backdropFilter: 'blur(8px)'
        }}
      />
    );
  }

  return (
    <>
      <Flex
        align="center"
        justify="center"
        gap={20}
        style={{width: '100%'}}
        vertical={true}
      >
        {isLoading ? (
          <Loader isHome={true} />
        ) : (
          <>
            <div
              style={{
                height: '100%',
                padding: '2rem',
                boxShadow: '0px 3px 6px #e0effd',
                borderRadius: '8px',
                backgroundColor: 'white',
                width: '100%'
              }}
            >
              <Flex
                gap="10px"
                align="center"
                justify="center"
                vertical={true}
                style={{width: '100%'}}
              >
                {selectedYear && (
                  <Flex
                    align="center"
                    justify="center"
                    vertical={false}
                    gap="10px"
                  >
                    <Select
                      style={{width: '120px'}}
                      placeholder="Year"
                      value={selectedYear}
                      options={firstYear}
                      size="middle"
                      popupMatchSelectWidth={false}
                      onChange={(e) => {
                        setSelectedYear(e);
                        setRefresh(true);
                        setFacilityId(undefined);
                        handleSearch({
                          name: 'selectedYear',
                          value: e
                        });
                      }}
                    />

                    <Select
                      showSearch
                      allowClear
                      placeholder="Facility Name"
                      value={facilityId}
                      defaultValue={undefined}
                      options={facilityDetails}
                      popupMatchSelectWidth={false}
                      size="middle"
                      onChange={(e, value) => {
                        setFacilityId(e);
                        setFacilitySelected(value);
                        setRefresh(true);
                        handleSearch({
                          name: 'facilityId',
                          value: e
                        });
                      }}
                      filterOption={filterSelectOptions}
                    />

                    {/* <Select
                      showSearch
                      allowClear
                      placeholder="Bill Type"
                      options={[
                        {label: 'Purchase Bill', value: 'purchase_bill'},
                        {label: 'Utility Bill', value: 'utility_bill'},
                        {label: 'Fuel Bill', value: 'fuel_bill'}
                      ]}
                      size="middle"
                      popupMatchSelectWidth={false}
                      onChange={(e) => {
                        setUploadType(e);
                      }}
                      filterOption={filterSelectOptions}
                    /> */}
                    <Cascader
                      options={submitConfigOptions}
                      onChange={(value) => {
                        setUploadType(value?.slice(-1)[0]);
                      }}
                      style={{width: 'max-content'}}
                      placeholder="Select Section"
                      placement={'bottomLeft'}
                      expandTrigger="hover"
                    />
                  </Flex>
                )}
                <Upload
                  accept="image/jpeg,image/png,image/jpg,application/pdf"
                  customRequest={(option) => {
                    const {file, onSuccess, onError} = option;
                    setIsUploading(true);
                    uploadDocsFile(file, uploadType, facilityId, selectedYear)
                      .then((res) => {
                        if (onSuccess) onSuccess('ok');
                        setRefresh(true);
                        setIsUploading(false);
                        showMessage(res);
                      })
                      .catch((err) => {
                        if (onError) {
                          onError(err);
                        }
                      });
                  }}
                  showUploadList={false}
                >
                  <Button
                    icon={<UploadOutlined />}
                    disabled={!(selectedYear && uploadType && facilityId)}
                    loading={isUploading}
                  >
                    {isUploading ? 'Uploading...' : 'Click to Upload'}
                  </Button>
                </Upload>
              </Flex>
            </div>
            <PageContent>
              <Flex align="center" justify="center" gap={10} vertical={true}>
                <Button
                  icon={<ReloadOutlined />}
                  type="text"
                  onClick={() => {
                    if (facilityId && selectedYear) {
                      setRefresh(true);
                    }
                  }}
                  disabled={refresh || !(facilityId && selectedYear)}
                  style={{alignSelf: 'flex-end'}}
                />

                <Table
                  columns={columns}
                  style={{width: '100%'}}
                  dataSource={dataSource}
                  rowKey="_id"
                  scroll={{x: 'scroll'}}
                  loading={{
                    indicator: <Loader />,
                    spinning: refresh
                  }}
                />
              </Flex>
            </PageContent>
          </>
        )}
      </Flex>
      {/* <Modal
        open={showModel}
        onOk={() => {
          setShowModel(false);
          //   setImgRef(false);
          //   setShowData(undefined);
        }}
        centered
        onCancel={() => {
          setShowModel(false);
          //   setImgRef(false);
          //   setShowData(undefined);
        }}
        width={'70%'}
        className="ocr-modal"
      >
        {!showData ? (
          <Loader />
        ) : (
          <Flex vertical={false} align="center" justify="center">
            <img
              //   src={showData.location}
              ref={imageRef}
              src={showData.location}
              style={{
                // backgroundColor: '#f3f7ff',
                width: '60%',
                height: '60%',
                flex: 1
              }}
              onLoad={handleImageLoad}
            />
            {bb.length > 0 &&
              bb.map(
                (
                  item: {
                    Left: number;
                    Top: number;
                    Width: number;
                    Height: number;
                    percent: number;
                  },
                  id: number
                ) => {
                  return (
                    <BoundingBox
                      key={id}
                      {...item}
                      imageHeight={imageDimensions.height}
                      imageWidth={imageDimensions.width}
                    />
                  );
                }
              )}
            <Flex
              style={{
                flex: 1,
                // width: '100%',
                height: '100%',
                padding: '20px',
                alignSelf: 'flex-start'
              }}
            >
              <Row gutter={[10, 10]}>
                {showData?.fields
                  ?.filter((a: any) => {
                    return a.boundingBox;
                  })
                  .map((item: any, index: number) => {
                    return Array.isArray(item.value) ? (
                      <Col span={24}>
                        <CustomTable data={item} />
                      </Col>
                    ) : (
                      <Col span={8} key={index}>
                        <div>
                          <Typography.Title level={5}>
                            {item.label}
                          </Typography.Title>
                          <Input type="textarea" value={item.value} />
                        </div>
                      </Col>
                    );
                  })}
              </Row>
            </Flex>
          </Flex>
        )}
      </Modal> */}
    </>
  );
};

export default OcrMain;
